import { AssetType, ImageType } from '@ps/utils/config'
import { generateUUID, getExt } from '@ps/utils/game'
import { Base64 } from 'js-base64'
import {
  getFileName,
  removeFileExtension,
  replaceJpegToJpg,
} from '@com/utils/file'
import qc from 'qc'
import { getBinUuidFromFiles } from '@ps/bridge/qc/bin'
import { splicingFileName } from './image'
import { saveFile } from '@ps/api/editor/file'
import JSZip from 'jszip'
import {
  addDragonBoneNode,
  getDragonBonePathPrefix,
  replaceDragonBoneNode,
} from '../qc/node/dragon-bone'

interface IFileData {
  name: string
  data: string
}
interface IDragonBoneComponent {
  // file_type: string;
  // res_type:string;
  files: Array<IFileData>
  type: number
  name: string
}
interface IAnimation {
  json: string
  ani: string
  [picType: string]: string
}
interface IDargonMeta {
  uuid: string
  type: number
  animationType: 1 | 2 | 3
  source: Array<keyof IAnimation>
}
function handleFiles({
  dbName,
  animation,
  picType,
}: {
  dbName: string
  animation: IAnimation
  picType: string
}): Array<IFileData> {
  const binContent = parseDragonBone({ animation })
  const files: Array<IFileData> = [
    {
      name: dbName + '.bin',
      data: Base64.encode(binContent),
    },
    {
      name: dbName + '.' + picType,
      data: animation[picType].replace(/data:.*base64,/, ''),
    },
    {
      name: dbName + '.ani',
      data: Base64.encode(animation['ani']),
    },
    {
      name: dbName + '.json',
      data: Base64.encode(animation['json']),
    },
  ].map((file) => {
    return {
      ...file,
      // 由于引擎不支持 jpeg 后缀，所以需要将 jpeg 改为 jpg
      name: replaceJpegToJpg(file.name),
    }
  })
  return files
}

export async function unzip(file: File): Promise<Array<IFileData>> {
  const zip = await JSZip.loadAsync(file)
  const fileNames = Object.keys(zip.files).filter(
    // TODO
    (fileName) =>
      (fileName.endsWith('.png') ||
        fileName.endsWith('.json') ||
        fileName.endsWith('.json')) &&
      !fileName.startsWith('__MACOSX'),
  )
  const data = await Promise.all(
    fileNames
      .map((fileName) => zip.file(fileName)!.async('base64'))
      .filter(Boolean),
  )
  const files = fileNames.map<IFileData>((fileName, index) => {
    return {
      name: getFileName(fileName),
      data: data[index],
    }
  })
  return files
}
export function parseDragonBone({
  animation,
}: {
  animation: IAnimation
}): string {
  const imgType = Object.keys(animation).find((item) => {
    return item !== 'ani' && item !== 'json'
  })!
  const meta = {} as IDargonMeta
  meta.uuid = generateUUID()
  // meta.uuid = getMd5ByString(animation[imgType]).slice(0, 15)
  meta.type = AssetType.ASSET_ATLAS
  const json = JSON.parse(animation['ani'])
  // 抄青瓷的,和我们理解的动画概念可能有点不同,反正能够运行龙骨就行了
  if (json && json.animations) {
    meta.animationType = 1 // 动作文件根路径就有 animations 才是普通动画
  } else if (json && json.samples) {
    meta.animationType = 3 // 动作文件根路径就有 samples 认为是帧采样动画
  } else {
    meta.animationType = 2 // 骨骼动画
  }
  meta.source = ['.json', '.' + imgType, '.ani']
  const arr = []
  arr.push(JSON.stringify(meta))
  arr.push(animation.json)
  arr.push(animation[imgType])
  arr.push(animation.ani)
  const binContent = JSON.stringify(arr)
  return binContent
}

export function generateAnimationData(files: Array<IFileData>): {
  animation: IAnimation
  picType: string
  error: string
} {
  const animation = {} as IAnimation
  let picType!: string
  let error = ''
  files.forEach((file) => {
    const fileType = getExt(file.name)
    if (fileType === 'json') {
      const rawText = Base64.decode(file.data)
      const jsonData = JSON.parse(rawText)
      if (typeof jsonData.frameRate !== 'undefined') {
        animation.ani = rawText
      } else {
        animation.json = rawText
      }
    } else if (ImageType.includes(fileType)) {
      picType = fileType
      animation[fileType] = file.data
    }
  })
  if (
    typeof animation['ani'] === 'undefined' ||
    typeof animation['json'] === 'undefined' ||
    typeof animation[picType] === 'undefined'
  ) {
    error = '龙骨上传必需2个json和1个图片文件'
  }
  return { animation, picType, error }
}
export async function addResourceToPlayable(data: {
  files: Array<IFileData>
  name: string
  pid: number
  binUUID: string
}) {
  const { files, pid, binUUID, name } = data
  const pathPrefix = getDragonBonePathPrefix(name)
  const promises = files.map((item) => {
    const finalName = splicingFileName({
      name: item.name,
      uuid: binUUID,
    })
    const path = pathPrefix + finalName
    return saveFile(
      {
        pid: Number(pid),
        path,
        enc: 'base64',
      },
      item.data,
    )
  })

  // 上传组件的文件
  await Promise.all(promises)
}

export function cookDragonConfigByData(data: IDragonBoneComponent) {
  const { animation, picType, error } = generateAnimationData(data.files)
  if (error) {
    throw new Error('file-error')
  }
  const files = handleFiles({
    dbName: removeFileExtension(data.files[0].name),
    animation,
    picType,
  })
  const fileName = removeFileExtension(files[0].name)
  const binUUID = getBinUuidFromFiles({
    files,
    binFileName: fileName + '.bin',
  })
  return {
    files,
    binUUID,
    fileName,
  }
}

export async function addDragonBone({
  data,
  pid,
  sceneNode,
  isAddNode = true,
}: {
  data: IDragonBoneComponent
  pid: number
  sceneNode: qc.Node
  isAddNode?: boolean
}): Promise<qc.Node | boolean> {
  const { name } = data
  const { files, binUUID, fileName } = cookDragonConfigByData(data)
  await addResourceToPlayable({
    files,
    name: fileName,
    pid,
    binUUID,
  })
  // 不添加节点
  if (!isAddNode) return true

  return await addDragonBoneNode({
    binUUID,
    pid,
    sceneNode,
    nodeName: name,
    name: fileName,
  })
}

export async function replaceDragonBone({
  data,
  pid,
  activeNode,
}: {
  data: IDragonBoneComponent
  pid: number
  activeNode: qc.Node
}) {
  const { name } = data
  const { animation, picType, error } = generateAnimationData(data.files)
  if (error) {
    throw new Error('file-error')
  }
  const files = handleFiles({
    dbName: removeFileExtension(data.files[0].name),
    animation,
    picType,
  })
  const fileName = removeFileExtension(files[0].name)
  const binUUID = getBinUuidFromFiles({
    files,
    binFileName: fileName + '.bin',
  })

  await addResourceToPlayable({
    files,
    name: fileName,
    pid,
    binUUID,
  })
  return await replaceDragonBoneNode({
    binUUID,
    pid,
    nodeName: name,
    name: fileName,
    activeNode,
  })
}
